/**
 * DemandPoolPage (S3.2 + SR3.1) — a route-derived TWO-LENS surface on one PO-
 * drafting flow (`/auth/purchase-orders/demand-pool/:lens`):
 *
 * - 訂單短缺 (shortage, the default / bare path): still-unallocated remainders of
 *   confirmed SO lines (open qty = ordered − Σ Allocation qty), grouped
 *   vendor×product; selecting rows raises ONE draft PO per vendor via the S3.1
 *   planner (an aggregated PO line whose Allocations span its member SO lines).
 *   Breaker hints (date-window span + container CBM) suggest splits, never block.
 *   Rows without a preferred vendor are flagged and unselectable (US-266).
 *
 * - 補貨建議 (reorder, SR3.1): the NetSuite Order-Items-page analog as a review
 *   queue of computed ROP suggestions (SR1.1 engine, derived-never-stored). It is
 *   gated INSIDE the page on FeatureGate `reorder.suggestions` — flag-off renders
 *   ZERO new UI (no lens switcher, only the shortage pool). Every quantity is a
 *   NetSuite read-through (可供量 @ syncedAt) or a Tier-2 aggregate; freshness is
 *   owned by the 可供量 SourceStampTag, the 低信心 badge rolls up only the
 *   non-freshness causes (variance + thin history). Severity renders tone + icon +
 *   label per SemanticType; 覆蓋天數 is NEUTRAL (no colour-by-value). Selecting rows
 *   → 加入需求池 (SR2.1 top-up) or 建立採購單草稿 (allocation-free draft PO carrying the
 *   edited qty). NO automation — a suggestion never creates a PO without the planner.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  App,
  Alert,
  Button,
  InputNumber,
  Result,
  Segmented,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import type { TableProps } from 'antd';
import {
  ArrowLeftOutlined,
  ExclamationCircleFilled,
  InfoCircleOutlined,
  PlusOutlined,
  WarningFilled,
  WarningOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/common/PageHeader';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import PurpleTag from '@/components/common/PurpleTag';
import SourceStampTag from '@/components/common/SourceStampTag';
import { extractApiError } from '@/hooks/useSalesOrders';
import {
  CONTAINER_CBM_20GP,
  CONTAINER_CBM_40HQ,
  DEMAND_DATE_WINDOW_DAYS,
  computeDemandBreakerHints,
  useDemandPool,
  usePurchaseOrderMutations,
  useReorderSuggestions,
  useSendToPool,
} from '@/hooks/usePurchaseOrders';
import type { DemandRow } from '@/hooks/usePurchaseOrders';
import { useFeature } from '@/features/flags';
import {
  REORDER_SEVERITY_SEMANTICS,
  type ReorderSeverity,
  type SemanticType,
} from '@/lib/reorder/computeReorderSuggestion';
import type { ReorderSuggestionRow } from '@/types/api/reorder';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { formatMoney } from '../salesOrders/components/soFormat';
import ReplenishmentDrawer from './ReplenishmentDrawer';

const { Text } = Typography;

type LensKey = 'shortage' | 'reorder';

// ---------------------------------------------------------------------------
// GA4 funnel — queue view → qty adjusted → send-to-pool → converted-to-draft-PO.
// A no-op when GA is not present (mock/dev); every call site below is reached
// ONLY on the reorder lens, which exists only when `reorder.suggestions` is on —
// so flag-off fires nothing by construction (SR3.1 acceptance).
// ---------------------------------------------------------------------------

type Gtag = (command: 'event', name: string, params?: Record<string, unknown>) => void;

function fireReorderFunnel(event: string, params?: Record<string, unknown>): void {
  const gtag = (window as unknown as { gtag?: Gtag }).gtag;
  if (typeof gtag === 'function') gtag('event', event, params);
}

// ===========================================================================
// 訂單短缺 — shortage pool lens (the S3.2 surface, unchanged behaviour)
// ===========================================================================

/** View row: base vendor×product rows, optionally merged by SCN group. */
interface DemandViewRow {
  key: string;
  vendorId: number | null;
  vendorName: string | null;
  scn: string | null;
  baseRows: DemandRow[];
}

function buildViewRows(rows: DemandRow[], mergeByScn: boolean): DemandViewRow[] {
  if (!mergeByScn) {
    return rows.map((row) => ({
      key: row.key,
      vendorId: row.vendorId,
      vendorName: row.vendorName,
      scn: row.scn,
      baseRows: [row],
    }));
  }
  const grouped = new Map<string, DemandViewRow>();
  for (const row of rows) {
    const key = `${row.vendorId ?? 'unassigned'}|${row.scn ?? row.productId}`;
    const existing = grouped.get(key);
    if (existing) existing.baseRows.push(row);
    else
      grouped.set(key, {
        key,
        vendorId: row.vendorId,
        vendorName: row.vendorName,
        scn: row.scn,
        baseRows: [row],
      });
  }
  return [...grouped.values()];
}

const sum = (values: Array<number | null>): number =>
  values.reduce((total: number, value) => total + (value ?? 0), 0);

const ShortagePoolLens = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['procurement', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();

  const { rows, isLoading, error, refresh } = useDemandPool();
  const { createPurchaseOrder } = usePurchaseOrderMutations();

  const [mergeByScn, setMergeByScn] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [creating, setCreating] = useState(false);

  const viewRows = useMemo(() => buildViewRows(rows, mergeByScn), [rows, mergeByScn]);

  const selectedBaseRows = useMemo(
    () =>
      viewRows
        .filter((row) => selectedKeys.includes(row.key))
        .flatMap((row) => row.baseRows),
    [viewRows, selectedKeys]
  );

  const hints = useMemo(() => computeDemandBreakerHints(selectedBaseRows), [selectedBaseRows]);
  const selectedLineCount = useMemo(
    () => selectedBaseRows.reduce((count, row) => count + row.members.length, 0),
    [selectedBaseRows]
  );

  const handleCreate = async () => {
    if (selectedBaseRows.length === 0) return;
    setCreating(true);
    try {
      // One draft PO per vendor; each base row = one (possibly aggregated)
      // PO line whose Allocations span its member SO lines.
      const byVendor = new Map<number, DemandRow[]>();
      for (const row of selectedBaseRows) {
        if (row.vendorId == null) continue;
        byVendor.set(row.vendorId, [...(byVendor.get(row.vendorId) ?? []), row]);
      }
      const created: string[] = [];
      for (const [vendorId, vendorRows] of byVendor) {
        const po = await createPurchaseOrder({
          vendorId,
          lines: vendorRows.map((row) => ({
            productId: row.productId,
            qty: row.openQty,
            allocations: row.members.map((member) => ({
              soLineId: member.soLineId,
              qty: member.openQty,
            })),
          })),
        });
        created.push(po.id);
      }
      message.success(t('procurement:demandPool.messages.created', { count: created.length }));
      setSelectedKeys([]);
      refresh();
      if (created.length === 1) navigate(`/auth/purchase-orders/${created[0]}`);
      else navigate('/auth/purchase-orders');
    } catch (err) {
      const { message: detail } = extractApiError(err);
      message.error(detail || t('common:messages.createFailed'));
    } finally {
      setCreating(false);
    }
  };

  const columns: TableProps<DemandViewRow>['columns'] = [
    {
      title: t('procurement:demandPool.fields.vendor'),
      key: 'vendor',
      width: 250,
      render: (_: unknown, record) =>
        record.vendorName ? (
          <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
            <span>{record.vendorName}</span>
            <PurpleTag style={{ margin: 0 }}>{t('procurement:fields.preferredVendor')}</PurpleTag>
          </div>
        ) : (
          <Tooltip title={t('procurement:demandPool.unassignedVendorHint')}>
            <Tag icon={<WarningOutlined />} color="orange" style={{ margin: 0 }}>
              {t('procurement:demandPool.unassignedVendor')}
            </Tag>
          </Tooltip>
        ),
    },
    {
      title: t('procurement:demandPool.fields.product'),
      key: 'product',
      width: 280,
      render: (_: unknown, record) => (
        <div className="tw-flex tw-flex-col tw-gap-1">
          {record.baseRows.map((row) => (
            <div key={row.productId} className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
              <span style={{ fontWeight: token.fontWeightStrong }}>{row.sku}</span>
              <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
                {row.productName}
              </span>
              {row.scn && <PurpleTag style={{ margin: 0 }}>{row.scn}</PurpleTag>}
              {/* Reorder-sourced top-up rows carry the 補貨 source chip (SR2.1). */}
              {row.source === 'reorder_suggestion' && (
                <PurpleTag style={{ margin: 0 }}>
                  {t('procurement:demandPool.reorder.sourceChip')}
                </PurpleTag>
              )}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: t('procurement:demandPool.fields.soRefs'),
      key: 'soRefs',
      width: 280,
      render: (_: unknown, record) => (
        <div className="tw-flex tw-flex-wrap tw-gap-1">
          {record.baseRows
            .flatMap((row) => row.members)
            .map((member) => (
              <Tag
                key={member.soLineId}
                style={{ margin: 0, cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/auth/sales-orders/${member.soId}`);
                }}
              >
                {member.soNumber} ×{member.openQty}
              </Tag>
            ))}
        </div>
      ),
    },
    {
      title: t('procurement:demandPool.fields.expectedShip'),
      key: 'expectedShip',
      width: 150,
      render: (_: unknown, record) => {
        const dates = record.baseRows
          .flatMap((row) => row.members.map((member) => member.expectedShipDate))
          .filter((date): date is string => Boolean(date))
          .sort();
        if (dates.length === 0) return renderDash();
        const first = dates[0];
        const last = dates[dates.length - 1];
        return first === last ? first : `${first} – ${last}`;
      },
    },
    {
      title: t('procurement:demandPool.fields.openQty'),
      key: 'openQty',
      width: 110,
      align: 'right',
      render: (_: unknown, record) => (
        <span style={{ fontWeight: token.fontWeightStrong, fontVariantNumeric: 'tabular-nums' }}>
          {sum(record.baseRows.map((row) => row.openQty))}
        </span>
      ),
    },
    {
      title: t('procurement:demandPool.fields.unitCost'),
      key: 'unitCost',
      width: 130,
      align: 'right',
      render: (_: unknown, record) => {
        const [first] = record.baseRows;
        return first?.unitCost != null && first.currency
          ? formatMoney(first.currency, first.unitCost)
          : renderDash();
      },
    },
    {
      title: t('procurement:demandPool.fields.cbm'),
      key: 'cbm',
      width: 100,
      align: 'right',
      render: (_: unknown, record) => {
        const anyMissing = record.baseRows.some((row) => row.totalCbm == null);
        if (anyMissing) return renderDash();
        return (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {sum(record.baseRows.map((row) => row.totalCbm)).toFixed(2)}
          </span>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader
        title={t('procurement:demandPool.title')}
        extra={
          <div className="tw-flex tw-items-center tw-gap-2">
            <Switch checked={mergeByScn} onChange={setMergeByScn} />
            <Text>{t('procurement:demandPool.mergeByScn')}</Text>
          </div>
        }
      />

      {/* Selection summary + breaker hints (suggestions, never blockers) */}
      {selectedBaseRows.length > 0 && (
        <div className="tw-mb-4 tw-flex tw-flex-col tw-gap-2">
          <div className="tw-flex tw-items-center tw-gap-4 tw-flex-wrap">
            <Text style={{ fontWeight: token.fontWeightStrong }}>
              {t('procurement:demandPool.selection.selected', { count: selectedLineCount })}
            </Text>
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('procurement:demandPool.hints.cbmSummary', {
                cbm: hints.totalCbm.toFixed(2),
                c20: CONTAINER_CBM_20GP,
                c40: CONTAINER_CBM_40HQ,
              })}
            </Text>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              loading={creating}
              onClick={() => void handleCreate()}
              style={{
                marginLeft: 'auto',
                borderRadius: radiusTokens.card,
                height: `${controlHeightTokens.controlHeightXL}px`,
              }}
            >
              {t('procurement:demandPool.selection.createDraftPo')}
            </Button>
          </div>
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {t('procurement:demandPool.selection.perVendorNote')}
          </Text>
          {hints.dateWindowExceeded && hints.dateSpanDays != null && (
            <Alert
              type="warning"
              showIcon
              message={t('procurement:demandPool.hints.dateWindow', {
                days: hints.dateSpanDays,
                limit: DEMAND_DATE_WINDOW_DAYS,
              })}
            />
          )}
          {hints.cbmExceeds40Hq && (
            <Alert
              type="warning"
              showIcon
              message={t('procurement:demandPool.hints.cbmOver40', {
                cbm: hints.totalCbm.toFixed(2),
                limit: CONTAINER_CBM_40HQ,
              })}
            />
          )}
          {hints.rowsMissingCbm > 0 && (
            <Alert
              type="info"
              showIcon
              message={t('procurement:demandPool.hints.missingDims', {
                count: hints.rowsMissingCbm,
              })}
            />
          )}
        </div>
      )}

      <TableContainer
        errorState={{
          active: Boolean(error),
          title: t('procurement:demandPool.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<DemandViewRow>
          rowKey="key"
          dataSource={viewRows}
          columns={columns}
          loading={isLoading}
          rowSelection={{
            selectedRowKeys: selectedKeys,
            onChange: setSelectedKeys,
            getCheckboxProps: (record) => ({
              // vendorId is REQUIRED (US-266) — unassigned rows can't convert.
              disabled: record.vendorId == null,
            }),
          }}
          locale={{
            emptyText: (
              <ListEmptyState
                title={t('procurement:demandPool.states.empty')}
                description={t('procurement:demandPool.states.emptyHint')}
              />
            ),
          }}
          pagination={false}
          scroll={{ x: 1300 }}
        />
      </TableContainer>
    </>
  );
};

// ===========================================================================
// 補貨建議 — reorder review-queue lens (SR3.1)
// ===========================================================================

const SEVERITY_TAG_COLOR: Record<SemanticType['tone'], string | undefined> = {
  danger: 'error',
  warning: 'warning',
  info: 'default',
};

function severityIcon(iconKey: SemanticType['iconKey']): React.ReactNode {
  if (iconKey === 'alert') return <ExclamationCircleFilled />;
  if (iconKey === 'warning') return <WarningFilled />;
  return <InfoCircleOutlined />;
}

const ReorderQueueLens = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['procurement', 'products', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const [searchParams, setSearchParams] = useSearchParams();

  // Fetch the FULL triggered worklist (no-preferred-vendor rows surfaced flagged,
  // US-266) and filter client-side — the worklist is small (no pagination), so
  // the filter option sets stay stable regardless of the active filters.
  const { suggestions, isLoading, error, refresh } = useReorderSuggestions({
    includeNoPreferredVendor: true,
  });
  const { sendToPool } = useSendToPool();
  const { createPurchaseOrder } = usePurchaseOrderMutations();

  const [vendorFilter, setVendorFilter] = useState<number | null>(null);
  const [severityFilter, setSeverityFilter] = useState<ReorderSeverity | null>(null);
  const [minQty, setMinQty] = useState<number | null>(null);
  const [noVendorOnly, setNoVendorOnly] = useState(false);
  // `?productId=` deep-link scope (canonical internalProductId, SR2.2 parity).
  const productFilter = searchParams.get('productId');

  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [editedQty, setEditedQty] = useState<Record<string, number>>({});
  const [drawerRow, setDrawerRow] = useState<ReorderSuggestionRow | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [convertedCount, setConvertedCount] = useState(0);

  // The five-source compose (SR0.1 snapshot · occ · demand-pool · vendor line ·
  // SR1.3 stats) is assembled server-side, so a partial-source failure would be
  // surfaced on the response envelope. The DEGRADED render path is defined up
  // front here; it stays empty until the SR2.1 response carries the signal.
  const partialSources: string[] = [];

  // GA4 `reorder_queue_view` — fire once per lens entry, after the first
  // successful load (gated: this component mounts only when the flag is on).
  const viewFiredRef = useRef(false);
  useEffect(() => {
    if (!isLoading && !error && !viewFiredRef.current) {
      viewFiredRef.current = true;
      fireReorderFunnel('reorder_queue_view', { count: suggestions.length });
    }
  }, [isLoading, error, suggestions.length]);

  const vendorOptions = useMemo(() => {
    const map = new Map<number, string>();
    for (const row of suggestions) {
      if (row.primaryVendorId != null) {
        map.set(row.primaryVendorId, row.vendorName ?? String(row.primaryVendorId));
      }
    }
    return [...map.entries()].map(([value, label]) => ({ value, label }));
  }, [suggestions]);

  const rows = useMemo(
    () =>
      suggestions.filter((row) => {
        if (productFilter && row.internalProductId !== productFilter) return false;
        if (noVendorOnly && row.primaryVendorId !== null) return false;
        if (vendorFilter != null && row.primaryVendorId !== vendorFilter) return false;
        if (severityFilter != null && row.severity !== severityFilter) return false;
        if (minQty != null && row.suggestedQty < minQty) return false;
        return true;
      }),
    [suggestions, productFilter, noVendorOnly, vendorFilter, severityFilter, minQty]
  );

  const selectableProductIds = useMemo(
    () => rows.filter((row) => row.primaryVendorId != null).map((row) => row.productId),
    [rows]
  );
  const selectedRows = useMemo(
    () => rows.filter((row) => selectedKeys.includes(row.productId)),
    [rows, selectedKeys]
  );

  const hasExpired = suggestions.some((row) => row.freshness === 'expired');
  const suggestedCount = suggestions.length;

  const qtyFor = (row: ReorderSuggestionRow): number => editedQty[row.productId] ?? row.suggestedQty;

  const clearProductFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('productId');
    setSearchParams(next, { replace: true });
  };

  const handleQtyChange = (row: ReorderSuggestionRow, value: number | null) => {
    const next = value == null ? row.suggestedQty : value;
    setEditedQty((prev) => ({ ...prev, [row.productId]: next }));
    if (next !== row.suggestedQty) {
      fireReorderFunnel('reorder_qty_adjusted', {
        productId: row.productId,
        from: row.suggestedQty,
        to: next,
      });
    }
  };

  const handleSendToPool = async () => {
    const ids = selectedRows
      .filter((row) => row.primaryVendorId != null)
      .map((row) => row.productId);
    if (ids.length === 0) return;
    setBusy(true);
    try {
      const result = await sendToPool(ids);
      const pooled = result.data.filter((item) => item.status === 'pooled').length;
      fireReorderFunnel('reorder_send_to_pool', { count: pooled });
      message.success(
        t('procurement:demandPool.reorder.selection.selected', { count: pooled })
      );
      setSelectedKeys([]);
      refresh();
    } catch (err) {
      const { message: detail } = extractApiError(err);
      message.error(detail || t('common:messages.createFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleCreateDraftPo = async () => {
    const chosen = selectedRows.filter((row) => row.primaryVendorId != null);
    if (chosen.length === 0) return;
    setBusy(true);
    try {
      // One draft PO per vendor; suggestion rows are ALLOCATION-FREE lines
      // (sourceSoId null, zero allocations) carrying the planner's edited qty.
      const byVendor = new Map<number, ReorderSuggestionRow[]>();
      for (const row of chosen) {
        const vendorId = row.primaryVendorId as number;
        byVendor.set(vendorId, [...(byVendor.get(vendorId) ?? []), row]);
      }
      const created: string[] = [];
      for (const [vendorId, vendorRows] of byVendor) {
        const po = await createPurchaseOrder({
          vendorId,
          lines: vendorRows.map((row) => ({ productId: row.productId, qty: qtyFor(row) })),
        });
        created.push(po.id);
      }
      setConvertedCount((count) => count + chosen.length);
      fireReorderFunnel('reorder_converted_to_po', {
        count: chosen.length,
        pos: created.length,
      });
      message.success(t('procurement:demandPool.messages.created', { count: created.length }));
      setSelectedKeys([]);
      refresh();
      if (created.length === 1) navigate(`/auth/purchase-orders/${created[0]}`);
      else navigate('/auth/purchase-orders');
    } catch (err) {
      const { message: detail } = extractApiError(err);
      message.error(detail || t('common:messages.createFailed'));
    } finally {
      setBusy(false);
    }
  };

  const columns: TableProps<ReorderSuggestionRow>['columns'] = [
    {
      title: t('procurement:demandPool.reorder.columns.product'),
      key: 'product',
      width: 240,
      render: (_: unknown, row) => (
        <div className="tw-flex tw-flex-col tw-gap-1">
          <Button
            type="link"
            style={{
              padding: 0,
              height: 'auto',
              fontWeight: token.fontWeightStrong,
              textAlign: 'left',
            }}
            onClick={() => {
              setDrawerRow(row);
              setDrawerOpen(true);
            }}
          >
            {row.sku}
          </Button>
          <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
            <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
              {row.productName}
            </span>
            {row.scn && <PurpleTag style={{ margin: 0 }}>{row.scn}</PurpleTag>}
          </div>
        </div>
      ),
    },
    {
      title: t('procurement:demandPool.reorder.columns.availableQty'),
      key: 'availableQty',
      width: 170,
      render: (_: unknown, row) => (
        <div className="tw-flex tw-flex-col tw-gap-1">
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: token.fontWeightStrong }}>
            {row.inventoryPosition - row.qtyOpenToReceive}
          </span>
          <div className="tw-flex tw-items-center tw-gap-1 tw-flex-wrap">
            <SourceStampTag
              sourceLabel={t('products:itemLocations.source')}
              syncedAt={row.syncedAt}
            />
            {row.freshness !== 'fresh' && (
              <Tooltip title={t('procurement:demandPool.reorder.banners.expired')}>
                <WarningOutlined style={{ color: token.colorWarning }} />
              </Tooltip>
            )}
          </div>
        </div>
      ),
    },
    {
      title: t('procurement:demandPool.reorder.columns.onOrderQty'),
      key: 'onOrderQty',
      width: 120,
      align: 'right',
      render: (_: unknown, row) => (
        <div className="tw-flex tw-flex-col tw-items-end tw-gap-1">
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{row.qtyOpenToReceive}</span>
          {row.confidenceCauses.variance && (
            <Tooltip
              title={t('procurement:demandPool.reorder.variance.reference', {
                qty: row.netsuiteQtyOnOrder ?? 0,
              })}
            >
              <Tag color="warning" style={{ margin: 0 }}>
                {t('procurement:demandPool.reorder.variance.chip')}
              </Tag>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: t('procurement:demandPool.reorder.columns.pooledDemand'),
      key: 'pooledDemand',
      width: 110,
      align: 'right',
      render: (_: unknown, row) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{row.pooledDemandQty}</span>
      ),
    },
    {
      title: `${t('procurement:demandPool.reorder.columns.reorderPoint')} / ${t('procurement:demandPool.reorder.columns.targetLevel')}`,
      key: 'ropPsl',
      width: 170,
      render: (_: unknown, row) => (
        <Space direction="vertical" size={4}>
          <Tag style={{ margin: 0, fontVariantNumeric: 'tabular-nums' }}>
            {t('procurement:demandPool.reorder.columns.reorderPoint')} {row.rop}
          </Tag>
          <Tag style={{ margin: 0, fontVariantNumeric: 'tabular-nums' }}>
            {t('procurement:demandPool.reorder.columns.targetLevel')} {row.psl}
          </Tag>
        </Space>
      ),
    },
    {
      title: t('procurement:demandPool.reorder.columns.suggestedQty'),
      key: 'suggestedQty',
      width: 150,
      render: (_: unknown, row) => (
        <div className="tw-flex tw-flex-col tw-gap-1" onClick={(e) => e.stopPropagation()}>
          <Tooltip
            title={
              row.moqClamped || row.multipleRounded
                ? t('procurement:demandPool.reorder.suggestedQtyRoundedFrom', { raw: row.rawQty })
                : undefined
            }
          >
            <InputNumber
              value={qtyFor(row)}
              min={0}
              precision={0}
              size="large"
              style={{ width: '100%' }}
              onChange={(value) => handleQtyChange(row, value)}
            />
          </Tooltip>
          {row.moq != null && (
            <Tag style={{ margin: 0, fontVariantNumeric: 'tabular-nums' }}>
              {t('procurement:demandPool.reorder.moqChip', { qty: row.moq })}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: t('procurement:demandPool.reorder.columns.coverageDays'),
      key: 'coverageDays',
      width: 110,
      align: 'right',
      render: (_: unknown, row) => {
        // NEUTRAL column — no colour-by-value; divide-by-zero → —/>365d (SR1.3).
        const text =
          row.coverageDays == null
            ? t('procurement:demandPool.reorder.coverage.none')
            : row.coverageDays >= 365
              ? t('procurement:demandPool.reorder.coverage.over')
              : `${row.coverageDays}d`;
        return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{text}</span>;
      },
    },
    {
      title: t('procurement:demandPool.reorder.columns.severity'),
      key: 'severity',
      width: 130,
      render: (_: unknown, row) => {
        const semantic = REORDER_SEVERITY_SEMANTICS[row.severity];
        return (
          <Tag icon={severityIcon(semantic.iconKey)} color={SEVERITY_TAG_COLOR[semantic.tone]}>
            {t(semantic.labelKey)}
          </Tag>
        );
      },
    },
    {
      title: t('procurement:demandPool.reorder.columns.primaryVendor'),
      key: 'primaryVendor',
      width: 180,
      render: (_: unknown, row) =>
        row.vendorName ? (
          <span>{row.vendorName}</span>
        ) : (
          // Surface WHY + a path to fix — not a bare greyed row (US-266).
          <div className="tw-flex tw-flex-col tw-gap-1 tw-items-start">
            <Tag icon={<WarningOutlined />} style={{ margin: 0 }}>
              {t('procurement:demandPool.reorder.noPreferredVendor.label')}
            </Tag>
            <Button
              type="link"
              style={{ padding: 0, height: 'auto' }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/auth/products/${row.productId}?section=vendors`);
              }}
            >
              {t('procurement:demandPool.reorder.noPreferredVendor.assign')}
            </Button>
          </div>
        ),
    },
    {
      title: t('procurement:demandPool.reorder.columns.confidence'),
      key: 'confidence',
      width: 140,
      render: (_: unknown, row) => {
        // Roll up ONLY the non-freshness causes; FRESHNESS is owned by the
        // 可供量 SourceStampTag (one cause never lights two chips — adjustment 2).
        const { variance, thinHistory } = row.confidenceCauses;
        if (!variance && !thinHistory) return renderDash();
        const reasons = [
          variance && t('procurement:demandPool.reorder.confidenceBadge.reasonVariance'),
          thinHistory && t('procurement:demandPool.reorder.confidenceBadge.reasonThinHistory'),
        ]
          .filter(Boolean)
          .join(' · ');
        return (
          <Tooltip title={reasons}>
            <Tag icon={<InfoCircleOutlined />} color="warning">
              {t('procurement:demandPool.reorder.confidenceBadge.low')}
            </Tag>
          </Tooltip>
        );
      },
    },
  ];

  const headerStat =
    suggestedCount > 0 || convertedCount > 0 ? (
      <Space size="large">
        {suggestedCount > 0 && (
          <Text type="secondary">
            {t('procurement:demandPool.reorder.header.suggested', { count: suggestedCount })}
          </Text>
        )}
        {convertedCount > 0 && (
          <Text type="secondary">
            {t('procurement:demandPool.reorder.header.converted', { count: convertedCount })}
          </Text>
        )}
      </Space>
    ) : undefined;

  return (
    <>
      <PageHeader
        title={t('procurement:demandPool.reorder.lensLabel')}
        extra={headerStat}
      />

      {/* Hard-ERROR — a defined state, not the empty table (SR3.1 states). */}
      {error ? (
        <Result
          status="warning"
          title={t('procurement:demandPool.reorder.states.loadFailed')}
          extra={
            <Button type="primary" onClick={() => void refresh()}>
              {t('procurement:demandPool.reorder.states.retry')}
            </Button>
          }
        />
      ) : (
        <>
          {/* Expired snapshot — advisory banner; rows stay actionable. */}
          {hasExpired && (
            <Alert
              type="warning"
              showIcon
              className="tw-mb-4"
              message={t('procurement:demandPool.reorder.banners.expired')}
            />
          )}

          {/* PARTIAL-SOURCE-FAILURE — defined up front (degraded, not discovered). */}
          {partialSources.length > 0 && (
            <Alert
              type="warning"
              showIcon
              className="tw-mb-4"
              message={t('procurement:demandPool.reorder.states.partialSource', {
                source: partialSources.join('、'),
              })}
            />
          )}

          {/* Filters toolbar */}
          <div className="tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            <Select
              allowClear
              size="large"
              style={{ width: 200 }}
              placeholder={t('procurement:demandPool.reorder.filters.vendor')}
              value={vendorFilter ?? undefined}
              options={vendorOptions}
              onChange={(value) => setVendorFilter(value ?? null)}
            />
            <Select
              allowClear
              size="large"
              style={{ width: 180 }}
              placeholder={t('procurement:demandPool.reorder.filters.severity')}
              value={severityFilter ?? undefined}
              options={(['high', 'medium', 'low'] as ReorderSeverity[]).map((value) => ({
                value,
                label: t(`procurement:demandPool.reorder.severity.${value}`),
              }))}
              onChange={(value) => setSeverityFilter((value as ReorderSeverity) ?? null)}
            />
            <InputNumber
              size="large"
              min={0}
              precision={0}
              style={{ width: 160 }}
              placeholder={t('procurement:demandPool.reorder.filters.minQty')}
              value={minQty ?? undefined}
              onChange={(value) => setMinQty(value ?? null)}
            />
            <div className="tw-flex tw-items-center tw-gap-2">
              <Switch checked={noVendorOnly} onChange={setNoVendorOnly} />
              <Text>{t('procurement:demandPool.reorder.filters.noPreferredVendor')}</Text>
            </div>
            {productFilter && (
              <Tag closable onClose={clearProductFilter} color="processing" style={{ margin: 0 }}>
                {t('procurement:demandPool.reorder.columns.product')}: {productFilter}
              </Tag>
            )}
            <Space className="tw-ml-auto">
              <Button
                onClick={() => setSelectedKeys([...selectableProductIds])}
                disabled={selectableProductIds.length === 0}
              >
                {t('procurement:demandPool.reorder.selection.markAll')}
              </Button>
              <Button
                onClick={() => setSelectedKeys([])}
                disabled={selectedKeys.length === 0}
              >
                {t('procurement:demandPool.reorder.selection.unmarkAll')}
              </Button>
            </Space>
          </div>

          {/* Selection summary + actions (加入需求池 / allocation-free draft PO) */}
          {selectedRows.length > 0 && (
            <div className="tw-mb-4 tw-flex tw-items-center tw-gap-4 tw-flex-wrap">
              <Text style={{ fontWeight: token.fontWeightStrong }}>
                {t('procurement:demandPool.reorder.selection.selected', {
                  count: selectedRows.length,
                })}
              </Text>
              <Space className="tw-ml-auto">
                <Button
                  size="large"
                  loading={busy}
                  onClick={() => void handleSendToPool()}
                  style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
                >
                  {t('procurement:demandPool.reorder.selection.sendToPool')}
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  size="large"
                  loading={busy}
                  onClick={() => void handleCreateDraftPo()}
                  style={{
                    borderRadius: radiusTokens.card,
                    height: `${controlHeightTokens.controlHeightXL}px`,
                  }}
                >
                  {t('procurement:demandPool.selection.createDraftPo')}
                </Button>
              </Space>
            </div>
          )}

          <TableContainer>
            <Table<ReorderSuggestionRow>
              rowKey="productId"
              dataSource={rows}
              columns={columns}
              loading={isLoading}
              rowSelection={{
                selectedRowKeys: selectedKeys,
                onChange: setSelectedKeys,
                getCheckboxProps: (record) => ({
                  // No primary vendor → cannot pool / convert (US-266).
                  disabled: record.primaryVendorId == null,
                }),
              }}
              locale={{
                emptyText: (
                  // Empty queue deep-links to the products list — set 補貨參數 there.
                  <ListEmptyState
                    title={t('procurement:demandPool.reorder.states.empty')}
                    description={t('procurement:demandPool.reorder.states.emptyHint')}
                    actionLabel={t('products:list.title')}
                    onAction={() => navigate('/auth/products')}
                  />
                ),
              }}
              pagination={false}
              scroll={{ x: 1500 }}
            />
          </TableContainer>
        </>
      )}

      <ReplenishmentDrawer
        open={drawerOpen}
        suggestion={drawerRow}
        onClose={() => setDrawerOpen(false)}
      />
    </>
  );
};

// ===========================================================================
// Wrapper — route-derived lens selection (the 補貨建議 lens is flag-gated)
// ===========================================================================

const DemandPoolPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['procurement', 'common']);
  const { lens } = useParams<{ lens?: string }>();
  const reorderEnabled = useFeature('reorder.suggestions');

  // Flag-off → always the shortage lens; the switcher is hidden (zero new UI).
  const activeLens: LensKey = reorderEnabled && lens === 'reorder' ? 'reorder' : 'shortage';

  return (
    <div className="tw-px-6 tw-pb-8">
      {/* Back to the PO list */}
      <div className="tw-mb-2">
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/auth/purchase-orders')}
          style={{ padding: 0, height: 'auto', lineHeight: 1 }}
        >
          {t('procurement:demandPool.backToList')}
        </Button>
      </div>

      {/* Route-derived lens switcher — only when the reorder queue is enabled. */}
      {reorderEnabled && (
        <div className="tw-mb-4">
          <Segmented<LensKey>
            value={activeLens}
            onChange={(value) => navigate(`/auth/purchase-orders/demand-pool/${value}`)}
            options={[
              {
                label: t('procurement:demandPool.reorder.shortageLensLabel'),
                value: 'shortage',
              },
              { label: t('procurement:demandPool.reorder.lensLabel'), value: 'reorder' },
            ]}
          />
        </div>
      )}

      {activeLens === 'reorder' ? <ReorderQueueLens /> : <ShortagePoolLens />}
    </div>
  );
};

export default DemandPoolPage;
